import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { CirclePacking, CirclePackingCanvasComponent } from '@innovation-os-ng/ui/chart-ng';
import { LoaderModule } from '@innovation-os-ng/ui/loader';
import { RangePickerModule } from '@innovation-os-ng/ui/range-picker';
import { TitleCasePipe } from '@innovation-os-ng/ui/utils';
import { CompoundFilter, findLeafByOn, LeafFilter } from '@itonics/filters';
import { TranslateModule } from '@itonics/ngx-translate-simple';
import { NgSelectModule } from '@ng-select/ng-select';
import { Unsubscribable } from 'rxjs';
import { metricSwitchOptionsMap } from '../../../definitions/metricSwitchOptions';
import { TrackedActions, TrackedEvents } from '../../../shared/constants/tracker-ids';
import { getTopicsQuery, queryDeselected } from '../../../shared/helpers/filter-helper';
import { ASYNC_METRIC_SWITCH_OPTIONS, MetricOptions, MetricsSwitch } from '../../../shared/model/metric';
import {
  DefaultMetricType,
  DocumentsQueryAggregation,
  FilterChangeAction,
  SignalsFilters,
  SignalsType,
  SignalsVisualization
} from '../../../shared/model/types';
import { AggregateTopicsService } from '../../../shared/modules/shared-services/services/aggregate-topics.service';
import { InsightsTrackingService } from '../../../shared/modules/shared-services/services/insights-tracking-service';
import { MetricService } from '../../../shared/modules/shared-services/services/metric.service';
import { DefaultMeticOptions } from '../../constants/default-metric';

@Component({
  selector: 'signals-topic-clusters',
  templateUrl: './topic-clusters.component.html',
  styleUrls: ['./topic-clusters.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    LoaderModule,
    NgSelectModule,
    FormsModule,
    TranslateModule,
    TitleCasePipe,
    RangePickerModule,
    CirclePackingCanvasComponent
  ]
})
export class TopicClustersComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private documentsFilter: CompoundFilter;
  private documentsFilterChanges: Unsubscribable;

  @Input()
  set filter(filter: CompoundFilter) {
    if (!filter) {
      return;
    }
    this.unsubscribe();
    this.documentsFilter = filter;
    this.subscribe();
    this.getAggregateData();
    this.setModel();
  }

  @Output()
  filterChanged: EventEmitter<{
    action: FilterChangeAction;
    filter: LeafFilter;
  }> = new EventEmitter();

  @Output()
  loadingChanged: EventEmitter<boolean> = new EventEmitter();

  /*
   * DATA STATES
   */

  public noData: boolean;
  private lastRequestTime: number;
  public get loading(): boolean {
    return Boolean(this.lastRequestTime);
  }

  /**
   *  DATA & MODEL
   */
  private data: CirclePacking;
  public filteredData: CirclePacking;
  public model: Array<string>;

  public insightsType = SignalsType;

  /**
   * Global Property:
   * Is the active property that changes according with the visualization
   * and sets active panel in bar chart, this global property set also a
   * global set of colors to be used in the same type of data in the entire application
   */

  @Output()
  changeGlobalProperty: EventEmitter<SignalsType> = new EventEmitter();

  @Input()
  set activeVisualization(activeVisualization: SignalsVisualization) {
    if (activeVisualization === SignalsVisualization.CIRCLE_PACKING) {
      this.changeGlobalProperty.emit(SignalsType.CATEGORY);
    }
  }

  // Trackable actions enum
  public TrackedActions = TrackedActions;

  /*
    Slider
  */
  public sliderConfig;

  private metricChangeSubs: Unsubscribable;

  /*
    Metric Switch
  */
  public metricSwitch: MetricsSwitch;
  public metricsSwitchEnum = MetricsSwitch;
  public metricOptions: Array<DefaultMetricType & { disabled?: boolean }> = DefaultMeticOptions;

  private topicClusterMetric: MetricOptions;

  constructor(
    private readonly aggregateTopicsService: AggregateTopicsService,
    private readonly trackingService: InsightsTrackingService,
    private readonly metricService: MetricService
  ) {}

  ngOnInit() {
    /*

      We subscribe to changes in the global metric
      to re-fetch data
    */
    this.metricChangeSubs = this.metricService.onMetricChange(() => {
      this.topicClusterMetric = null;
      this.metricSwitch = this.metricService.getMetricSwitch();

      this.getAggregateData();
    });

    /*
      Assign global metric when topic cluster is first initialized
    */
    this.metricSwitch = this.metricService.getMetricSwitch();

    // Update the metric switch options whenever some of them should be disabled/enabled
    // (the computationally-expensive options are disabled when there are too many signals in the current query)
    this.metricService.complexMetricsDisabled.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((disabled) => {
      this.metricOptions = this.metricOptions.map((option) => {
        option.disabled = disabled && ASYNC_METRIC_SWITCH_OPTIONS.indexOf(option.value) !== -1;
        return option;
      });
    });
  }

  ngOnDestroy() {
    this.unsubscribe();
    this.metricChangeSubs.unsubscribe();
  }

  private unsubscribe() {
    if (this.documentsFilterChanges) {
      this.documentsFilterChanges.unsubscribe();
    }
  }

  private subscribe() {
    this.documentsFilterChanges = this.documentsFilter.onChanged((changedFilter: CompoundFilter) => {
      this.getAggregateData();

      if (queryDeselected(changedFilter, SignalsType.TOPICS)) {
        this.model = null;
      } else {
        this.setModel();
      }
    });
  }

  private getAggregateData() {
    // The filters that we want for
    // Mark as loading with timestamp
    const timestamp = Date.now();
    this.lastRequestTime = timestamp;
    this.loadingChanged.emit(this.loading);

    // a copy of the combined filter
    const filter = this.documentsFilter.clone();
    //  We want to remove anu topic query from the selection
    const filterSelection = filter.getQuery(SignalsFilters.SELECTION) as CompoundFilter;
    // if there is no selection inside, nothing to highlight
    if (filterSelection) {
      // we don't want to filter down the result data with this very filter we
      // manage in this instance of the component, so remove it from the selection
      const query = filterSelection.findQuery(findLeafByOn(SignalsType.TOPICS)) as LeafFilter;
      if (query) {
        filterSelection.removeFilter(query);
      }
    }

    this.aggregateTopicsService
      .getAggregateTopics(filter, this.topicClusterMetric || this.metricService.getGlobalMetric(), 'topics-clusters', 'topics')
      .then((res) => {
        if (res.topics && res.topics.length) {
          this.data = {
            id: 'root',
            label: 'topics',
            children: res.topics
          };

          this.filteredData = {
            id: 'root',
            label: 'topics',
            children: res.topics
          };

          this.initSlider(res.topics);
        } else {
          this.data = null;
          this.filteredData = null;
          this.sliderConfig = null;
        }

        // Only end loading if this was the last request sent
        if (this.lastRequestTime === timestamp) {
          this.lastRequestTime = null;
        }
        this.loadingChanged.emit(this.loading);
        this.noData = !res.topics || !res.topics.length;
      });
  }

  private initSlider(topics: Array<DocumentsQueryAggregation>) {
    const valueArray = topics.map((d) => d.doc_count);
    const max = Math.max(...valueArray);
    const min = Math.min(...valueArray);
    this.sliderConfig = {
      minValue: min,
      maxValue: max,
      options: {
        floor: min,
        ceil: max
      }
    };
  }

  public constrainData(event) {
    this.filteredData = {
      id: 'root',
      label: 'topics',
      children: this.data.children.filter((d) => d.doc_count >= event.min && d.doc_count <= event.max)
    };
    this.trackingService.trackEvent(TrackedEvents.CLUSTER_SLIDER_USED);
  }

  private setModel() {
    const topicQuery = getTopicsQuery(this.documentsFilter, SignalsFilters.SELECTION);

    // Our topics visualization doesn't support exclusion, so for now we don't store the exclusion in the model
    if (!topicQuery || topicQuery.getExclude()) {
      return;
    }

    this.model = topicQuery.getValues() as Array<string>;
  }

  public select(categories: Array<string>) {
    const filter = new LeafFilter(SignalsType.TOPICS);
    if (categories && categories.length) {
      filter.setValues(categories);
    }

    this.unsubscribe();

    // to avoid calling our own callback
    this.filterChanged.next({
      action: categories && categories.length ? FilterChangeAction.ADD : FilterChangeAction.REMOVE,
      filter
    });

    this.subscribe();
  }

  /**
   * Track a generic 'visualization' event (Topic Clusters only reference in dimension4)
   *
   * @param name Event being tracked
   */
  public trackEvent(name: string) {
    this.trackingService.trackEvent(`${TrackedEvents.PREFIX_VIZ}.${name}`, { dimension4: 'Topic Clusters' });
  }

  /**
   *
   * @param value type of metric Relevance or doc counts
   */
  public onSwitchChange(value: MetricsSwitch) {
    this.topicClusterMetric = metricSwitchOptionsMap[value];
    this.getAggregateData();
  }
}
